import {apiFetch} from "../client";

export interface FinanceMoney { atomic: string; scale: number; token?: string; }
export interface FinanceSource { type: string; id: string; confidence: string; name?: string; provider?: string; }
export interface FinanceCollectionStatus { enabled: boolean; poll_interval_seconds: number; }
export interface FinancePage<T> { items: T[]; next_cursor?: string; collection?: FinanceCollectionStatus; }
export interface FinanceOperation {
    id: string; kind: string; status: string; occurred_at: string; recorded_at?: string; network?: string;
    from_address?: string; to_address?: string; amount?: FinanceMoney; fee?: FinanceMoney;
    accounted: boolean; evidence_quality: string; sources: FinanceSource[];
}
export function financeKindLabel(kind: string): string {
    if (kind === "paylord_batch_rate_credit") return "Пересчёт ставки Paylord";
    return kind === "tron_network_fee" ? "Сетевая комиссия TRX" : kind;
}
export interface FinanceOperationDetail extends FinanceOperation {
    paylord?: FinancePaylordDetail;
    related: FinanceSource[]; discrepancy_keys: string[]; movements: FinanceLinkedMovement[]; candidates?: FinanceLinkedMovement[]; team_withdrawal?: FinanceTeamWithdrawal; gasfree?: FinanceGasFreeDetail;
}
export interface FinancePaylordReceiptCandidate {
    operation_id: string; tx_id: string; event_index: number; to_address: string;
    occurred_at: string; received: FinanceMoney; difference?: FinanceMoney; confidence: string;
}
export interface FinancePaylordDetail {
    withdrawal_id: string; account_id: string; reserved?: FinanceMoney; platform_sent?: FinanceMoney;
    net_frozen_delta: FinanceMoney; status: string; candidates: FinancePaylordReceiptCandidate[];
}
export interface FinanceTeamWithdrawalSlice { id: string; merchant_id?: string; merchant_name?: string; requested: FinanceMoney; reset?: FinanceMoney; }
export interface FinanceTeamWithdrawal { request_id: string; requested: FinanceMoney; received: FinanceMoney; reset?: FinanceMoney; completed_at?: string; slices: FinanceTeamWithdrawalSlice[]; reset_movements?: FinanceLinkedMovement[]; }
export interface FinanceGasFreeDetail {
    provider_fact: FinanceSource; receipt_fact?: FinanceSource; transaction_id: string; payer_address: string;
    user_address: string; receiver_address: string; principal_event_index: number; principal: FinanceMoney;
    transfer_fee?: FinanceMoney; activation_fee?: FinanceMoney; total_fee?: FinanceMoney;
}
export interface FinanceLinkedMovement {
    source: FinanceSource; amount?: FinanceMoney; booked_at?: string; occurred_at?: string; created_at?: string;
    actor?: string; operation_key?: string; merchant?: FinanceSource; balance_after?: string; accounted: boolean;
    actor_name?: string; reason?: string; balance_owner?: FinanceSource;
}
export interface FinanceWallet {
    id: string; network: string; address: string; owner_kind: string; owner_id?: string; owner_name?: string;
    label: string; balance?: FinanceMoney; balance_observed_at?: string; balance_stale: boolean;
    balance_attempt_at?: string; balance_check_status?: string; balance_check_error?: string;
    scan_enabled: boolean; data_quality: string; coverage_from?: string; coverage_to?: string;
    income_atomic: string; outgoing_atomic: string; unknown_incoming_atomic?: string; observed_net_atomic?: string;
    unclassified_outgoing_atomic: string; fees_atomic: string; checkpoint_status: string;
    reconciliation?: FinanceWalletReconciliation;
}
export interface FinanceWalletReconciliation {
    status: "insufficient_data" | "matched" | "mismatch";
    reason: string;
    opening_block?: string; closing_block?: string;
    opening_time?: string; closing_time?: string;
    opening?: FinanceMoney; closing?: FinanceMoney; expected?: FinanceMoney; difference?: FinanceMoney;
}
export interface FinanceQuery {
    from?: string; to?: string; q?: string; kind?: string; merchant_id?: string; team_id?: string; wallet_id?: string;
    address?: string; status?: string; limit?: number; cursor?: string;
}

export const FINANCE_ACTION_KINDS = [
    "payment", "merchant_withdrawal", "team_withdrawal", "paylord_withdrawal", "book_row", "book_opening", "book_row_settlement",
    "platform_closed", "blockchain_deposit", "team_reset", "merchant_reset", "debt_correction", "manual", "backfill", "other",
] as const;
export type FinanceActionKind = typeof FINANCE_ACTION_KINDS[number];
export type FinanceActionNote = "team_via_book" | "team_waiting_book" | "underpaid" | "overpaid" | "closed_manually" | "balance_chain_broken" | "balance_unknown" | "net_zero" | "several_currencies" | "paylord_fee";
export interface FinanceActionStep {
    movement_id: string; reason: string; delta: string; before?: string; after?: string; occurred_at: string; recorded_at: string;
    operation_key?: string; actor: string; actor_name: string; counterparty?: FinanceSource;
}
export interface FinanceActionLeg {
    owner: FinanceSource; settlement_source?: string; archived: boolean; delta: FinanceMoney; before?: string; after?: string;
    contiguous: boolean; chain_broken: boolean; unknown: boolean; steps: FinanceActionStep[];
}
export interface FinanceActionTeamWithdrawal {
    request_id: string; status: string; requested: FinanceMoney; received: FinanceMoney; reset?: FinanceMoney;
    created_at: string; created_by: string; created_by_name?: string; completed_at?: string; completed_by?: string; completed_by_name?: string;
}
export interface FinanceActionMerchantWithdrawal {
    request_id: string; status: string; debit: FinanceMoney; received?: FinanceMoney; fee?: FinanceMoney; network: string; address: string;
    tx_hash?: string; rejection_reason?: string; created_at: string; updated_at: string;
}
export interface FinanceActionBookRow {
    id: string; source?: string; external_id?: string; operation_type?: string; external_order_id?: string; external_withdrawal_id?: string; operation_at?: string;
}
export const FINANCE_ACTION_GROUPS = ["orders", "team", "merchant", "adjustments"] as const;
export type FinanceActionGroup = typeof FINANCE_ACTION_GROUPS[number];
export interface FinanceActionOrder {
    number: string; side: "in" | "out" | string; workflow: string; status: string; payment_status: string; fiat: FinanceMoney; rate: string;
    created_at: string; claimed_at?: string; appealed_at?: string; appeal_reason?: string; payment_created_at: string; payment_updated_at: string;
    trader: string; team: string;
}
export interface FinanceActionTeamBook {
    status: "posted" | "waiting"; movements: number; posted_at?: string; amount?: FinanceMoney; team?: string; difference?: FinanceMoney; reconciled: boolean;
}
export interface FinanceAction {
    id: string; kind: FinanceActionKind | string; group: FinanceActionGroup | string; status?: string; occurred_at: string; actor: string; actor_name: string;
    direction: "in" | "out" | "credit" | "debit" | "none" | string; amount: FinanceMoney; legs: FinanceActionLeg[];
    operation_id?: string; order_number?: string; payment_id?: string; notes: (FinanceActionNote | string)[];
    team_withdrawal?: FinanceActionTeamWithdrawal; merchant_withdrawal?: FinanceActionMerchantWithdrawal; book_rows?: FinanceActionBookRow[];
    team_book?: FinanceActionTeamBook; order?: FinanceActionOrder;
    /** Ручная корректировка вывода из кабинета, которая на деле — комиссия Paylord: выводы, чью комиссию она закрывает, названный первым. */
    paylord_fee?: {withdrawals: string[]};
}
export interface FinanceActionPage {
    items: FinanceAction[]; next_cursor?: string; from: string; to: string; totals?: {actions: number; movements: number};
    groups: {all: number} & Record<FinanceActionGroup, number>;
}
export interface FinanceActionQuery {
    from?: string; to?: string; kind?: string; group?: string; q?: string; merchant_id?: string; team_id?: string; limit?: number; cursor?: string;
}
export interface FinanceBalanceSides { positive: FinanceMoney; positive_count: number; negative: FinanceMoney; negative_count: number; }
export interface FinanceBalanceSummary {
    from: string; to: string; token: string; other_currencies: number;
    groups: {key: "merchants" | "cabinets" | "teams"; opening: FinanceBalanceSides; closing: FinanceBalanceSides}[];
    items: {owner: FinanceSource; settlement_source?: string; opening: FinanceMoney; closing: FinanceMoney}[];
}

export type FinanceFlowPartyKind = "paylord" | "cabinet" | "team" | "merchant" | "external";
export interface FinanceFlowParty { address?: string; name?: string; kind: FinanceFlowPartyKind | string; }
export const FINANCE_FEE_GROUPS = ["paylord", "traders", "merchants"] as const;
export type FinanceFeeGroup = typeof FINANCE_FEE_GROUPS[number];
export interface FinanceWalletFlow {
    id: string; kind: "paylord_withdrawal" | "trader_payout" | "merchant_withdrawal" | string;
    at: string; network: string; tx_id?: string; from: FinanceFlowParty; to: FinanceFlowParty;
    sent?: FinanceMoney; received?: FinanceMoney; fee?: FinanceMoney; fee_group: FinanceFeeGroup | string;
    issue?: "receipt_missing" | "fee_unknown" | string; book_id?: string; book_at?: string;
}
export interface FinanceWalletCash {
    party: FinanceFlowParty; opening?: FinanceMoney; closing?: FinanceMoney; chain?: FinanceMoney; seen_to?: string; no_history?: boolean;
}
export interface FinanceWalletCashGroup { key: "paylord" | "merchants" | "teams" | string; opening?: FinanceMoney; chain?: FinanceMoney; wallets: FinanceWalletCash[]; }
export interface FinanceWalletFlowPage {
    from: string; to: string; token: string; items: FinanceWalletFlow[]; truncated: boolean;
    fees: {key: FinanceFeeGroup | string; fee: FinanceMoney; count: number}[]; fee_total: FinanceMoney; fee_unknown: number; cash: FinanceWalletCashGroup[];
}

function queryString(query: FinanceQuery | FinanceActionQuery = {}): string {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== "") params.set(key, String(value));
    });
    const encoded = params.toString();
    return encoded ? `?${encoded}` : "";
}

export function getFinanceOperation(token: string | null, id: string): Promise<FinanceOperationDetail> {
    return apiFetch(`/admin/finance/operations/${encodeURIComponent(id)}`, token);
}
export function listFinanceWallets(token: string | null, query?: FinanceQuery): Promise<FinancePage<FinanceWallet>> {
    return apiFetch(`/admin/finance/wallets${queryString(query)}`, token);
}
export function listFinanceActions(token: string | null, query?: FinanceActionQuery): Promise<FinanceActionPage> {
    return apiFetch(`/admin/finance/actions${queryString(query)}`, token);
}
export function getFinanceBalances(token: string | null, query?: Pick<FinanceActionQuery, "from" | "to">): Promise<FinanceBalanceSummary> {
    return apiFetch(`/admin/finance/balances${queryString(query)}`, token);
}
export function getFinanceWalletFlows(token: string | null, query?: Pick<FinanceActionQuery, "from" | "to">): Promise<FinanceWalletFlowPage> {
    return apiFetch(`/admin/finance/wallet-flows${queryString(query)}`, token);
}

// Мерчант считается по заявкам, трейдер — по своим платежам; average_rub есть только у мерчантов.
export interface FinanceStatisticsRow {
    owner: FinanceSource; team?: FinanceSource;
    turnover_usdt: FinanceMoney; turnover_rub: FinanceMoney;
    completed: number; cancelled: number; average_rub?: FinanceMoney;
}
export interface FinanceStatistics {
    from: string; to: string; token: string;
    merchants: FinanceStatisticsRow[]; merchant_total: FinanceStatisticsRow;
    traders: FinanceStatisticsRow[]; teams: FinanceStatisticsRow[]; trader_total: FinanceStatisticsRow;
}
export function getFinanceStatistics(token: string | null, query?: Pick<FinanceActionQuery, "from" | "to">): Promise<FinanceStatistics> {
    return apiFetch(`/admin/finance/statistics${queryString(query)}`, token);
}
